/**
 * Upload a recording made elsewhere: a call recorded by the phone, a voice memo, a file from a
 * second device. It becomes one meeting with one audio segment, and goes through the same
 * transcribe → summarize path as a live recording.
 */
import { ref, uploadBytes } from "firebase/storage";
import { byId, on, toast } from "../lib/dom";
import { storage } from "../lib/firebase";
import { currentUser } from "../data/auth";
import { createMeeting, updateMeeting, writeChunkMeta } from "../data/store";
import { finishUploadedMeeting } from "./meeting";

/** Most transcription services stop at 25 MB; keep a little headroom. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
const ACCEPT = "audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.mp4";

function extFor(file: File) {
  const byName = file.name.toLowerCase().match(/\.(m4a|mp3|wav|ogg|webm|aac|mp4)$/)?.[1];
  const byType = file.type.includes("mp4") || file.type.includes("aac") || file.type.includes("m4a") ? "m4a" : file.type.includes("mpeg") || file.type.includes("mp3") ? "mp3" : file.type.includes("wav") ? "wav" : file.type.includes("ogg") ? "ogg" : file.type.includes("webm") ? "webm" : "";
  const ext = byName ?? byType;
  return ext === "aac" || ext === "mp4" ? "m4a" : ext;
}

/** Duration from the browser's decoder, or 0 when it cannot read the file (the server still transcribes it). */
function readDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const a = document.createElement("audio");
    const url = URL.createObjectURL(file);
    const done = (n: number) => { URL.revokeObjectURL(url); resolve(Number.isFinite(n) ? Math.round(n) : 0); };
    a.preload = "metadata";
    a.onloadedmetadata = () => done(a.duration);
    a.onerror = () => done(0);
    window.setTimeout(() => done(0), 8000);
    a.src = url;
  });
}

/** Opens the file picker. The file has to be chosen inside a tap, so this is called from a click. */
export function pickRecording() {
  let input = byId<HTMLInputElement>("uploadRecordingInput");
  if (!input) {
    document.body.insertAdjacentHTML("beforeend", `<input type="file" id="uploadRecordingInput" accept="${ACCEPT}" hidden>`);
    input = byId<HTMLInputElement>("uploadRecordingInput")!;
    on(input, "change", () => { const f = input!.files?.[0]; input!.value = ""; if (f) void uploadRecording(f); });
  }
  input.click();
}

export async function uploadRecording(file: File) {
  const uid = currentUser()?.uid;
  if (!uid) return toast("Sign in first");
  const ext = extFor(file);
  if (!ext) return toast("That file is not an audio recording Timo can read. Try an m4a, mp3, wav or ogg file");
  if (file.size > MAX_UPLOAD_BYTES) return toast(`That recording is ${(file.size / 1048576).toFixed(0)} MB. Timo can take files up to 25 MB, about an hour of phone audio`);

  const name = file.name.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").trim() || `Recording · ${new Date().toLocaleDateString([], { month: "short", day: "numeric" })}`;
  toast("Uploading the recording…");
  let id = "";
  try {
    const durationSec = await readDuration(file);
    id = await createMeeting(name);
    const mime = file.type || `audio/${ext === "m4a" ? "mp4" : ext}`;
    const path = `meetings/${uid}/${id}/chunks/00000.${ext}`;
    await writeChunkMeta(id, 0, { state: "uploading", offsetSec: 0, durationSec, bytes: file.size, mime });
    await uploadBytes(ref(storage, path), file, {
      contentType: mime.split(";")[0],
      customMetadata: { seq: "0", offsetSec: "0", durationSec: String(durationSec), meetingId: id, uid },
    });
    await writeChunkMeta(id, 0, { state: "uploaded" });
    await updateMeeting(id, { chunkCount: 1, durationSec, status: "processing", endedAt: new Date() });
    await finishUploadedMeeting(id);
  } catch (err) {
    toast((err as Error).message || "The upload did not go through. Check your connection and try again");
    if (id) void updateMeeting(id, { status: "complete", summary: "The upload did not finish. Try again from Meetings." }).catch(() => {});
  }
}
